import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Cookie, X } from "lucide-react";

const KEY = "sam_cookie_consent_v1";
type Choice = "all" | "essential";

function applyConsent(choice: Choice) {
  const granted = choice === "all" ? "granted" : "denied";
  const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  g?.("consent", "update", {
    analytics_storage: granted,
    ad_storage: granted,
    ad_user_data: granted,
    ad_personalization: granted,
  });
  const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
  fbq?.("consent", choice === "all" ? "grant" : "revoke");
  if (choice !== "all") {
    // Remove any marketing/analytics cookies left from an earlier "Accept all".
    for (const c of document.cookie.split(";")) {
      const name = c.split("=")[0].trim();
      if (/^(_fbp|_fbc|_ga|_gid|_gat|_gcl)/.test(name)) {
        const host = location.hostname.replace(/^www\./, "");
        for (const d of ["", `; domain=${host}`, `; domain=.${host}`]) {
          document.cookie = `${name}=; Max-Age=0; path=/${d}`;
        }
      }
    }
  }
  window.dispatchEvent(new Event("sam-consent-change"));
}

export function CookieConsent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const es = pathname === "/es" || pathname.startsWith("/es/");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(KEY) as Choice | null;
    if (saved) {
      // Re-apply once tracking has loaded.
      const t = setTimeout(() => applyConsent(saved), 1500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  if (!open || pathname.startsWith("/admin")) return null;

  const choose = (c: Choice) => {
    localStorage.setItem(KEY, c);
    applyConsent(c);
    setOpen(false);
  };

  const t = es
    ? {
        title: "Su privacidad, a su manera",
        body: "Usamos cookies para que las calculadoras recuerden sus números y para entender qué contenido ayuda a los inversionistas. Nunca vendemos su información.",
        policy: "Política de privacidad",
        all: "Aceptar todas",
        ess: "Solo esenciales",
      }
    : {
        title: "Your privacy, your call",
        body: "We use cookies so our calculators remember your numbers and to learn which content helps investors most. We never sell your information.",
        policy: "Privacy policy",
        all: "Accept all",
        ess: "Essential only",
      };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500 sm:inset-x-6 sm:bottom-6"
    >
      <div className="relative overflow-hidden rounded-2xl border border-brand-gold/40 bg-gradient-to-br from-brand to-brand-dark p-5 text-brand-foreground shadow-2xl sm:p-6">
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-gold/15 blur-2xl" />
        <button
          onClick={() => choose("essential")}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1 text-brand-foreground/60 transition hover:bg-brand-foreground/10 hover:text-brand-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-start gap-3 sm:flex-1">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-gold/20 ring-1 ring-brand-gold/40">
              <Cookie className="h-5 w-5 text-brand-gold" />
            </span>
            <div className="pr-6 sm:pr-0">
              <p className="font-display text-base font-semibold">{t.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-brand-foreground/80">
                {t.body}{" "}
                <Link
                  to={es ? "/es/legal/privacidad" : "/legal/privacy"}
                  className="font-medium text-brand-gold underline-offset-2 hover:underline"
                >
                  {t.policy}
                </Link>
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
            <button
              onClick={() => choose("essential")}
              className="rounded-lg border border-brand-foreground/30 px-4 py-2.5 text-sm font-medium text-brand-foreground transition hover:bg-brand-foreground/10"
            >
              {t.ess}
            </button>
            <button
              onClick={() => choose("all")}
              className="rounded-lg bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary shadow-lg transition hover:bg-brand-gold-hover"
            >
              {t.all}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
